import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import useCostCenterApi from "../../http/costCenter/api";
import { CostCenter } from "../../http/costCenter/types";
import useOfferAcceptorsApi from "../../http/offerAcceptor/api";
import { OfferAcceptor } from "../../http/offerAcceptor/type";
import usePriorityApi from "../../http/priority/api";
import { Priority } from "../../http/priority/types";
import { ProcessingType } from "../../http/processingType/type";
import useSupplierApi from "../../http/supplier/api";
import { Supplier } from "../../http/supplier/types";
import { PaginationQuery } from "../../types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

export interface ProcessingRequestFormBody {
  id?: number;
  priority: Priority;
  supplier: Supplier | null;
  costCenter: CostCenter | null;
  pickingNumber: string | null;
  offerAcceptor: OfferAcceptor | null;
}

type ProcessingRequestFormProps = {
  processingRequest?: ProcessingRequestFormBody;
  offerMandatory: boolean;
  onSubmit: (data: ProcessingRequestFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  type: ProcessingType;
};

const EditProcessingRequestForm: FC<ProcessingRequestFormProps> = ({
  processingRequest,
  offerMandatory,
  onSubmit,
  isDisabled,
  isLoading,
  type,
}) => {
  const i18n = useI18n();

  const { searchPriorities } = usePriorityApi();
  const { searchSuppliers } = useSupplierApi();
  const { searchCostCenters } = useCostCenterApi();
  const { searchOfferAcceptors } = useOfferAcceptorsApi();

  const [defaultPrioritiesOptions, setDefaultPrioritiesOptions] = useState<Priority[]>([]);
  const [defaultSuppliersOptions, setDefaultSuppliersOptions] = useState<Supplier[]>([]);
  const [defaultCostCentersOptions, setDefaultCostCentersOptions] = useState<CostCenter[]>([]);
  const [defaultOfferAcceptorOptions, setDefaultOfferAcceptorOptions] = useState<OfferAcceptor[]>([]);

  const defaultValues = {
    priority: processingRequest?.priority,
    supplier: processingRequest?.supplier ?? null,
    costCenter: processingRequest?.costCenter ?? null,
    pickingNumber: processingRequest?.pickingNumber ?? null,
    offerAcceptor: processingRequest?.offerAcceptor ?? null,
  };

  return (
    <Form<ProcessingRequestFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ register, control, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [processingRequest]);

        return (
          <>
            <Controller
              name="priority"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.priority} error={fieldState.error}>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef<AbortController | undefined>(undefined);
                    const loadOptions = createLoadOptions<Priority>(aborter, searchTerm, searchPriorities);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultPrioritiesOptions);
                    }, []);

                    return (
                      <AsyncSelect<Priority>
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultPrioritiesOptions}
                        loadOptions={(st) => loadOptions(st)}
                        getOptionLabel={(o) => o.name}
                        getOptionValue={(o) => o.id}
                        placeholder={i18n.t.common.select + "..."}
                        isDisabled={isDisabled || isLoading}
                        styles={reactSelectStyles(isInvalid)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            {!!type && type.showMarketingFields && (
              <Controller
                name="supplier"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.suppliers.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<Supplier>(aborter, searchTerm, searchSuppliers);

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultSuppliersOptions);
                      }, []);

                      return (
                        <AsyncSelect<Supplier>
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultSuppliersOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => o.name}
                          getOptionValue={(o) => o.code}
                          placeholder={i18n.t.common.select + "..."}
                          isDisabled={isDisabled || isLoading}
                          styles={reactSelectStyles(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}

            <Controller
              name="costCenter"
              control={control}
              render={({ field }) => (
                <FormField label={i18n.t.costCenters.singular} isOptional>
                  {({ labelId, isInvalid }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef<AbortController | undefined>(undefined);
                    const loadOptions = createLoadOptions<CostCenter>(aborter, searchTerm, searchCostCenters);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultCostCentersOptions);
                    }, []);
                    return (
                      <AsyncSelect<CostCenter>
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultCostCentersOptions}
                        loadOptions={(st) => loadOptions(st)}
                        getOptionLabel={(o) => `${o.code} - ${o.name}`}
                        getOptionValue={(o) => o.code}
                        placeholder={i18n.t.common.select + "..."}
                        isDisabled={isDisabled || isLoading}
                        styles={reactSelectStyles(isInvalid)}
                      />
                    );
                  }}
                </FormField>
              )}
            />

            <FormField label={i18n.t.common.productionPickingNumber} isOptional>
              {({ labelId }) => (
                <Input id={labelId} {...register("pickingNumber")} disabled={isDisabled || isLoading} />
              )}
            </FormField>
            {offerMandatory && (
              <Controller
                name="offerAcceptor"
                control={control}
                // rules={{ required: offerMandatory }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.sendOfferTo} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef<AbortController | undefined>(undefined);
                      const loadOptions = createLoadOptions<OfferAcceptor>(
                        aborter,
                        searchTerm,
                        async (query: PaginationQuery, signal?: AbortSignal) => {
                          const data = await searchOfferAcceptors(query, signal);
                          return data;
                        },
                      );

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultOfferAcceptorOptions);
                      }, []);

                      return (
                        <AsyncSelect<OfferAcceptor>
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultOfferAcceptorOptions}
                          loadOptions={(st) => loadOptions(st)}
                          getOptionLabel={(o) => `${o.email}`}
                          getOptionValue={(o) => o.id}
                          placeholder={i18n.t.common.select + "..."}
                          isDisabled={isDisabled || isLoading}
                          styles={reactSelectStyles(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
          </>
        );
      }}
    </Form>
  );
};

export default EditProcessingRequestForm;
